import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { FileWarning, Plus, X, Loader2, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  reported: "bg-amber-50 text-amber-700 border-amber-200",
  assessed: "bg-sky-50 text-sky-700 border-sky-200",
  notifications_pending: "bg-rose-50 text-rose-700 border-rose-200",
  closed_not_a_breach: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABELS = {
  reported: "Reported", assessed: "Assessed — awaiting approval",
  notifications_pending: "Breach — notifications pending",
  closed_not_a_breach: "Closed — not a breach", closed: "Closed",
};

function ReportModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", discovered_at: new Date().toISOString().slice(0, 10),
    source: "internal", ba_name: "", ba_discovered_at: "", affected_individuals: 0, data_types: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        affected_individuals: parseInt(form.affected_individuals, 10) || 0,
        data_types: form.data_types.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/incidents", payload);
      onCreated(data);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="incident-report-modal">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Report a security incident</h3>
          <button onClick={onClose} data-testid="incident-modal-close"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="incident-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short title (e.g. Lost laptop)" className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          <textarea data-testid="incident-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What happened?" className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">Discovered on
              <input data-testid="incident-discovered" type="date" required value={form.discovered_at} onChange={(e) => setForm({ ...form, discovered_at: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            </label>
            <label className="text-sm">Individuals affected
              <input data-testid="incident-affected" type="number" min="0" value={form.affected_individuals} onChange={(e) => setForm({ ...form, affected_individuals: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            </label>
          </div>
          <input data-testid="incident-datatypes" value={form.data_types} onChange={(e) => setForm({ ...form, data_types: e.target.value })} placeholder="Data types (comma-separated, e.g. names, labs)" className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          <label className="text-sm block">Source
            <select data-testid="incident-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring">
              <option value="internal">Internal discovery</option>
              <option value="business_associate">Reported by a business associate</option>
            </select>
          </label>
          {form.source === "business_associate" && (
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="incident-ba-name" value={form.ba_name} onChange={(e) => setForm({ ...form, ba_name: e.target.value })} placeholder="Business associate name" className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
              <input data-testid="incident-ba-discovered" type="date" value={form.ba_discovered_at} onChange={(e) => setForm({ ...form, ba_discovered_at: e.target.value })} className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            </div>
          )}
          {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="incident-report-error">{err}</p>}
          <button type="submit" disabled={busy || !form.title} data-testid="incident-report-submit" className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Report incident
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Incidents() {
  const [incidents, setIncidents] = useState(null);
  const [modal, setModal] = useState(false);

  const load = () => api.get("/incidents").then((r) => setIncidents(r.data)).catch(() => setIncidents([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Incidents &amp; Breaches</h1>
          <p className="text-sm text-muted-foreground mt-1">Log, assess and notify on security incidents (45 CFR 164.400–414).</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="incident-new-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90">
          <Plus className="w-4 h-4" /> Report incident
        </button>
      </div>

      {incidents === null ? (
        <div className="h-40 bg-secondary rounded-lg animate-pulse" />
      ) : incidents.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="incidents-empty">
          <FileWarning className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No incidents logged. That's a good thing.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="incidents-list">
          {incidents.map((i) => (
            <Link key={i.id} to={`/incidents/${i.id}`} data-testid={`incident-row-${i.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-secondary/40 transition-colors">
              <div className="min-w-0">
                <p className="font-medium truncate">{i.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Discovered {i.discovered_at} · {i.affected_individuals} affected{i.source === "business_associate" ? ` · via ${i.ba_name || "BA"}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs font-medium rounded-full border px-2.5 py-0.5 ${STATUS_STYLES[i.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{STATUS_LABELS[i.status] || i.status}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {modal && <ReportModal onClose={() => setModal(false)} onCreated={() => { setModal(false); load(); }} />}
    </div>
  );
}
